import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "../styles/theme.css";
import Header from "../Components/Header.jsx";
import { 
  Download, 
  ChevronRight,  
  Code, 
  Palette,  
  Database, 
  TrendingUp, 
  Github, 
  Linkedin, 
  Facebook, 
  Twitter 
} from "lucide-react";

export default function Home() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem("theme") || "light";
    }
    return "light";
  });

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    
    const cursor = document.createElement("div");
    cursor.className = "custom-cursor";
    const follower = document.createElement("div");
    follower.className = "cursor-follower";
    document.body.appendChild(cursor);
    document.body.appendChild(follower);

    const moveCursor = (e) => {
      cursor.style.left = e.clientX + "px";
      cursor.style.top = e.clientY + "px";
      follower.style.left = e.clientX + "px";
      follower.style.top = e.clientY + "px";
    };

    window.addEventListener("mousemove", moveCursor);
    return () => {
      window.removeEventListener("mousemove", moveCursor);
      document.body.removeChild(cursor);
      document.body.removeChild(follower);
    };
  }, [theme]);

  const skills = [
    { icon: <Palette size={22} />, title: "Graphic Design", items: ["CorelDRAW", "Photoshop", "Canva", "Branding"] },
    { icon: <Code size={22} />, title: "Frontend", items: ["React", "JavaScript", "HTML5", "Bootstrap", "CSS3 / Tailwind"] },
    { icon: <Database size={22} />, title: "Backend", items: ["PHP", "SQL / MySQL", "REST API", "Database Design"] },
  ];

  const proficiency = [
    { name: "React & JavaScript", level: 90 },
    { name: "PHP & REST API", level: 85 },
    { name: "SQL & Database Design", level: 80 },
    { name: "CorelDRAW & Photoshop", level: 95 },
    { name: "HTML & CSS", level: 92 }
  ];

  return (
    <>
      <Header theme={theme} toggleTheme={toggleTheme} />
      <main className="page home">
        <div className="bg-glow-container">
          <div className="glow-shape blue"></div>
          <div className="glow-shape purple"></div>
        </div>

        {/* Hero Section */}
        <section className="hero">
          <motion.div 
            className="hero-text"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.span className="tag" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              Full-Stack Creative Developer
            </motion.span>
            
            <motion.h1>
              Israel Mrakpor
            </motion.h1>

            <motion.p className="hero-description">
              I build websites that feel effortless to use and are built to last. Based in <b>Nigeria</b> and currently studying at the <b>National Open University</b>, I connect thoughtful design with strong engineering. My goal is to create digital experiences that help your business grow while making life easier for your customers.
            </motion.p>

            {/* Social Media Handles */}
            <div className="hero-socials">
              <a href="https://github.com/Dktam18" target="_blank" rel="noreferrer" className="social-icon-link">
                <Github size={24} />
              </a>
              <a href="https://linkedin.com/in/israel-mrakpor-b40a21363/" target="_blank" rel="noreferrer" className="social-icon-link">
                <Linkedin size={24} />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61578464693137" target="_blank" rel="noreferrer" className="social-icon-link">
                <Facebook size={24} />
              </a>
              <a href="https://x.com/IsraelMrakpor" target="_blank" rel="noreferrer" className="social-icon-link">
                <Twitter size={24} />
              </a>
            </div>
            
            <div className="hero-actions">
              <a href="/cv.pdf" download className="btn primary flex-btn">Download CV <Download size={18} /></a>
              <a href="/projects" className="btn outline flex-btn">View Projects <ChevronRight size={18} /></a>
            </div>
          </motion.div>

          <motion.div className="hero-image-container" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}>
            <div className="hero-image glass">
              <img src="https://i.ibb.co/HDHDKLH7/Israel-Mrakpor-1.jpg" alt="Israel Mrakpor" />
            </div>
            <motion.div className="floating-badge badge-php" animate={{ y: [0, -15, 0] }} transition={{ repeat: Infinity, duration: 3 }}>PHP</motion.div>
            <motion.div className="floating-badge badge-react" animate={{ y: [0, 15, 0] }} transition={{ repeat: Infinity, duration: 4 }}>React</motion.div>
          </motion.div>
        </section>

        {/* Proficiency Level Section */}
        <section className="proficiency-section">
          <motion.div className="section-header" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <TrendingUp size={30} className="accent-icon" />
            <h2>Experience & Proficiency</h2>
            <p>A breakdown of my technical and design control</p>
          </motion.div>
          
          <div className="proficiency-container glass">
            {proficiency.map((skill, index) => (
              <div key={index} className="skill-progress-wrapper">
                <div className="skill-label">
                  <span>{skill.name}</span>
                  <span className="percent-text">{skill.level}%</span>
                </div>
                <div className="progress-track">
                  <motion.div 
                    className="progress-bar-fill"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${skill.level}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: index * 0.1, ease: "easeOut" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Skills Cards Grid */}
        <section className="skills-section">
          <div className="section-header">
            <h2>Technical/Creative Stack</h2>
          </div>
          <div className="skills-grid">
            {skills.map((skill, index) => (
              <motion.div key={skill.title} className="skill-card glass" whileHover={{ y: -10 }} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <div className="skill-icon">{skill.icon}</div>
                <h3>{skill.title}</h3>
                <div className="skill-items">
                  {skill.items.map((item) => <span key={item} className="skill-tag">{item}</span>)}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}